import React, { useState, useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";
import TextStyle from "@tiptap/extension-text-style";
import Highlight from "@tiptap/extension-highlight";
import Color from "@tiptap/extension-color";
import FontFamily from "@tiptap/extension-font-family";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getFirestore, collection, addDoc } from "firebase/firestore";
import {
  getStorage,
  ref,
  uploadBytesResumable,
  getDownloadURL,
} from "firebase/storage";
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  Heading1,
  Heading2,
  Image as ImageIcon,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Highlighter,
  ImageDown,
  X,
  Paintbrush,
  Code as CodeIcon,
  Link as LinkIcon,
  Link2Off as LinkOff,
  Trash,
} from "lucide-react";

import Link from "@tiptap/extension-link";

const MenuBar = ({ editor }) => {
  const [imageUrl, setImageUrl] = useState("");
  const [showImageModal, setShowImageModal] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  console.log("[MenuBar] Rendering with editor:", editor?.isEditable);

  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkText, setLinkText] = useState("");

  useEffect(() => {
    if (!editor) {
      console.error("[MenuBar] Editor not available");
      setError("Editor failed to initialize");
      return;
    }

    try {
      console.log("[MenuBar] Editor state:", {
        isEditable: editor.isEditable,
        isFocused: editor.isFocused,
        isEmpty: editor.isEmpty,
      });
    } catch (err) {
      console.error("[MenuBar] Error checking editor state:", err);
      setError("Editor state error");
    }
  }, [editor]);

  useEffect(() => {
    console.log("[MenuBar] Image modal state changed:", showImageModal);
    if (!showImageModal) {
      setImageUrl("");
      setPreviewUrl("");
      setError("");
    }
  }, [showImageModal]);

  const validateUrl = (url) => {
    try {
      console.log("[MenuBar] Validating URL:", url);
      const parsedUrl = new URL(url);
      const isValid = parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:";
      console.log("[MenuBar] URL validation result:", isValid);
      return isValid;
    } catch (err) {
      console.error("[MenuBar] URL validation error:", err);
      return false;
    }
  };

  const addLink = () => {
    console.log("[MenuBar] Adding link - URL:", linkUrl, "Text:", linkText);
    setError("");

    if (!editor) {
      console.error("[MenuBar] Editor not available for link insertion");
      setError("Editor not ready");
      return;
    }

    if (!linkUrl) {
      setError("Please enter a URL");
      return;
    }

    if (!validateUrl(linkUrl)) {
      setError("Please enter a valid URL starting with http:// or https://");
      return;
    }

    try {
      const selection = editor.state.selection;
      const selectedText = selection.empty
        ? null
        : editor.state.doc.textBetween(selection.from, selection.to);

      console.log("[MenuBar] Selected text for link:", selectedText);

      if (selectedText) {
        editor
          .chain()
          .focus()
          .extendMarkRange("link")
          .setLink({ href: linkUrl })
          .run();
      } else {
        if (!linkText) {
          setError("Please enter text to display for the link.");
          return;
        }

        editor
          .chain()
          .focus()
          .insertContent([
            {
              type: "text",
              text: linkText,
              marks: [
                {
                  type: "link",
                  attrs: {
                    href: linkUrl,
                  },
                },
              ],
            },
          ])
          .run();
      }

      console.log("[MenuBar] Link added successfully");
      setShowLinkModal(false);
      setLinkUrl("");
      setLinkText("");
    } catch (err) {
      console.error("[MenuBar] Error adding link:", err);
      setError("Failed to add link");
    }
  };

  const removeLink = () => {
    editor.chain().focus().unsetLink().run();
  };

  const validateAndPreviewImage = (url) => {
    setImageUrl(url);
    setError("");
    if (!url) return setPreviewUrl("");
    const img = new Image();
    img.onload = () => setPreviewUrl(url);
    img.onerror = () => {
      setPreviewUrl("");
      setError("Invalid image URL or failed to load.");
    };
    img.src = url;
  };

  const addImage = () => {
    if (imageUrl && !error) {
      editor.chain().focus().setImage({ src: imageUrl }).run();
      setShowImageModal(false);
    }
  };

  const handleImageUpload = async (e) => {
    console.log("[MenuBar] Starting image upload");
    const file = e.target.files?.[0];
    
    if (!file) {
      console.log("[MenuBar] No file selected");
      return;
    }

    if (!file.type.startsWith("image/")) {
      console.error("[MenuBar] Invalid file type:", file.type);
      setError("Invalid file type. Please select an image file.");
      return;
    }

    try {
      setLoading(true);
      const storage = getStorage();
      const storageRef = ref(storage, `images/${file.name}`);
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          console.log("[MenuBar] Upload progress:", progress);
        },
        (error) => {
          console.error("[MenuBar] Upload error:", error);
          setError("Upload failed: " + error.message);
          setLoading(false);
        },
        async () => {
          try {
            const url = await getDownloadURL(uploadTask.snapshot.ref);
            console.log("[MenuBar] Upload complete. URL:", url);
            setPreviewUrl(url);
            editor.chain().focus().setImage({ src: url }).run();
            setLoading(false);
          } catch (err) {
            console.error("[MenuBar] Error getting download URL:", err);
            setError("Failed to get image URL");
            setLoading(false);
          }
        }
      );
    } catch (err) {
      console.error("[MenuBar] Image upload error:", err);
      setError("Failed to upload image");
      setLoading(false);
    }
  };

  if (!editor) {
    console.log("[MenuBar] Rendering without editor");
    return null;
  }

  return (
    <>
      <div className="sticky top-28 left-0 z-10 flex flex-col flex-wrap max-h-[80vh] overflow-y-auto gap-2 p-2 bg-gray-100 border-r border-gray-300">
        {/* Text styles */}
        <button
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`p-2 rounded hover:bg-gray-200 ${
            editor.isActive("bold") ? "bg-gray-300" : ""
          }`}
        >
          <Bold className="w-4 h-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`p-2 rounded hover:bg-gray-200 ${
            editor.isActive("italic") ? "bg-gray-300" : ""
          }`}
        >
          <Italic className="w-4 h-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={`p-2 rounded hover:bg-gray-200 ${
            editor.isActive("underline") ? "bg-gray-300" : ""
          }`}
        >
          <UnderlineIcon className="w-4 h-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleHighlight().run()}
          className={`p-2 rounded hover:bg-gray-200 ${
            editor.isActive("highlight") ? "bg-yellow-200" : ""
          }`}
        >
          <Highlighter className="w-4 h-4" />
        </button>

        {/* Headings */}
        <button
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 1 }).run()
          }
          className={`p-2 rounded hover:bg-gray-200 ${
            editor.isActive("heading", { level: 1 }) ? "bg-gray-300" : ""
          }`}
        >
          <Heading1 className="w-4 h-4" />
        </button>
        <button
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 2 }).run()
          }
          className={`p-2 rounded hover:bg-gray-200 ${
            editor.isActive("heading", { level: 2 }) ? "bg-gray-300" : ""
          }`}
        >
          <Heading2 className="w-4 h-4" />
        </button>

        {/* Alignments */}
        <button
          onClick={() => editor.chain().focus().setTextAlign("left").run()}
          className={`p-2 rounded hover:bg-gray-200 ${
            editor.isActive({ textAlign: "left" }) ? "bg-gray-300" : ""
          }`}
        >
          <AlignLeft className="w-4 h-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().setTextAlign("center").run()}
          className={`p-2 rounded hover:bg-gray-200 ${
            editor.isActive({ textAlign: "center" }) ? "bg-gray-300" : ""
          }`}
        >
          <AlignCenter className="w-4 h-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().setTextAlign("right").run()}
          className={`p-2 rounded hover:bg-gray-200 ${
            editor.isActive({ textAlign: "right" }) ? "bg-gray-300" : ""
          }`}
        >
          <AlignRight className="w-4 h-4" />
        </button>

        {/* Lists */}
        <button
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`p-2 rounded hover:bg-gray-200 ${
            editor.isActive("bulletList") ? "bg-gray-300" : ""
          }`}
        >
          <List className="w-4 h-4" />
        </button>

        {/* Colors & Fonts */}
        <label className="p-2 rounded hover:bg-gray-200 cursor-pointer">
          <Paintbrush className="w-4 h-4" />
          <input
            type="color"
            className="hidden"
            onChange={(e) =>
              editor.chain().focus().setColor(e.target.value).run()
            }
          />
        </label>

        <select
          onChange={(e) =>
            editor.chain().focus().setFontFamily(e.target.value).run()
          }
          className="text-xs rounded p-1"
          defaultValue="inherit"
        >
          <option value="inherit">Default</option>
          <option value="Georgia, serif">Georgia</option>
          <option value="Arial, sans-serif">Arial</option>
          <option value="'Courier New', monospace">Courier New</option>
        </select>

        {/* Images */}
        <button
          onClick={() => setShowImageModal(true)}
          className="p-2 rounded hover:bg-gray-200"
        >
          <ImageIcon className="w-4 h-4" />
        </button>
        <label className="p-2 rounded hover:bg-gray-200 cursor-pointer">
          <ImageDown className="w-4 h-4" />
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleImageUpload}
          />
        </label>

        {/* Code Block */}
        <button
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          className={`p-2 rounded hover:bg-gray-200 ${
            editor.isActive("codeBlock") ? "bg-gray-300 text-white" : ""
          }`}
        >
          <CodeIcon className="w-4 h-4" />
        </button>

        <button
          onClick={() => setShowLinkModal(true)}
          className={`p-2 rounded hover:bg-gray-200 ${
            editor.isActive("link") ? "bg-gray-300" : ""
          }`}
          title="Add Link"
        >
          <LinkIcon className="w-4 h-4" />
        </button>
        {editor.isActive("link") && (
          <button
            onClick={removeLink}
            className="p-2 rounded hover:bg-gray-200 bg-red-100"
            title="Remove Link"
          >
            <LinkOff className="w-4 h-4" />
          </button>
        )}
      </div>

      {showLinkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Insert Link</h3>
              <button
                onClick={() => setShowLinkModal(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label
                  htmlFor="linkUrl"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  URL
                </label>
                <input
                  id="linkUrl"
                  type="text"
                  value={linkUrl}
                  onChange={(e) => {
                    setLinkUrl(e.target.value);
                    setError("");
                  }}
                  placeholder="https://example.com"
                  className="w-full border border-gray-300 p-2 rounded focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label
                  htmlFor="linkText"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Text (optional - will use selected text if available)
                </label>
                <input
                  id="linkText"
                  type="text"
                  value={linkText}
                  onChange={(e) => setLinkText(e.target.value)}
                  placeholder="Link text"
                  className="w-full border border-gray-300 p-2 rounded focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {error && <p className="text-red-500 text-sm mt-1">{error}</p>}

              <div className="flex justify-end pt-2">
                <button
                  onClick={() => setShowLinkModal(false)}
                  className="mr-2 px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={addLink}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Insert Link
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        /* Style code blocks */
        .ProseMirror pre {
          background-color: #1a1a1a;
          border-radius: 8px;
          padding: 1rem;
          margin: 1rem 0;
          overflow-x: auto;
        }

        .ProseMirror pre code {
          color: #ffffff;
          font-family: 'Courier New', Courier, monospace;
          font-size: 0.9rem;
          line-height: 1.5;
          width: 100%;
          display: block;
        }

        /* Style inline code */
        .ProseMirror code {
          background-color: #1a1a1a;
          color: #ffffff;
          padding: 0.2em 0.4em;
          border-radius: 4px;
          font-family: 'Courier New', Courier, monospace;
          font-size: 0.9em;
        }

        /* Add a subtle border */
        .ProseMirror pre {
          border: 1px solid #2d2d2d;
        }

        /* Style when code block is selected */
        .ProseMirror pre.selected {
          outline: 2px solid #4a9eff;
        }
      `}</style>

      {/* Image Modal */}
      {showImageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow max-w-lg w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Insert Image</h3>
              <button
                onClick={() => setShowImageModal(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <input
              type="text"
              value={imageUrl}
              onChange={(e) => validateAndPreviewImage(e.target.value)}
              placeholder="Paste image URL..."
              className="w-full border p-2 rounded mb-2"
            />
            {error && <p className="text-red-500">{error}</p>}
            {previewUrl && (
              <img
                src={previewUrl}
                alt="Preview"
                className="max-h-60 mx-auto my-2"
              />
            )}
            <div className="flex justify-end">
              <button
                onClick={addImage}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                Insert
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

MenuBar.propTypes = {
  editor: PropTypes.object,
};

const BLOG_CATEGORIES = [
  { id: 'dsa', name: 'Data Structures & Algorithms' },
  { id: 'java', name: 'Java Programming' },
  { id: 'python', name: 'Python' },
  { id: 'spring', name: 'Spring Framework' },
  { id: 'react', name: 'React.js' },
  { id: 'node', name: 'Node.js' },
  { id: 'database', name: 'Database Systems' },
  { id: 'system-design', name: 'System Design' },
  { id: 'cloud', name: 'Cloud Computing' },
  { id: 'devops', name: 'DevOps' },
  { id: 'security', name: 'Cybersecurity' },
  { id: 'ml', name: 'Machine Learning' },
  { id: 'web', name: 'Web Development' },
  { id: 'mobile', name: 'Mobile Development' },
  { id: 'business', name: 'Business & Management' },
  { id: 'soft-skills', name: 'Soft Skills' },
  { id: 'interview', name: 'Interview Preparation' },
  { id: 'quantitative', name: 'Quantitative Aptitude' },
  { id: 'articulation', name: 'Communication Skills' },
  { id: 'career', name: 'Career Guidance' }
];

const TextEditor = () => {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState(
    localStorage.getItem("editorContent") || "<p>Hello World!</p>"
  );
  const [category, setCategory] = useState("");
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const auth = getAuth();
  const db = getFirestore();

  useEffect(() => {
    console.log("[BlogEditor] Component mounted");
    console.log("[BlogEditor] Initial content:", content);
    console.log("[BlogEditor] LocalStorage content:", localStorage.getItem("editorContent"));
    
    try {
      console.log("[BlogEditor] Auth state:", auth.currentUser);
      console.log("[BlogEditor] Database instance:", db ? "Available" : "Not available");
    } catch (err) {
      console.error("[BlogEditor] Firebase initialization error:", err);
      setError("Failed to initialize Firebase");
    }

    return () => {
      console.log("[BlogEditor] Component unmounting");
    };
  }, []);

  useEffect(() => {
    console.log("[BlogEditor] Auth state change effect running");
    let unsubscribe;
    try {
      unsubscribe = onAuthStateChanged(auth, (u) => {
        console.log("[BlogEditor] Auth state changed:", u?.email);
        if (u) {
          setUser(u);
          console.log("[BlogEditor] User authenticated:", u.uid);
        } else {
          console.log("[BlogEditor] No user, redirecting to login");
          navigate("/login?redirect=blog-editor");
        }
      });
    } catch (err) {
      console.error("[BlogEditor] Auth subscription error:", err);
      setError("Authentication error occurred");
    }

    return () => unsubscribe?.();
  }, [auth, navigate]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        codeBlock: {
          HTMLAttributes: {
            class: 'bg-gray-900 text-gray-100 rounded-md p-4 font-mono my-4',
          },
          code: {
            HTMLAttributes: {
              class: 'text-gray-100',
            },
          },
        },
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'rounded-lg max-w-full h-auto',
        },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Underline,
      TextStyle,
      Color,
      Highlight.configure({
        multicolor: true,
      }),
      FontFamily,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-500 hover:text-blue-700 underline',
        },
      }),
    ],
    content: '',
    editorProps: {
      attributes: {
        class: 'prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none',
      },
    },
  });

  useEffect(() => {
  if (!editor) return;

  editor.on('update', () => {
    const html = editor.getHTML();
    setContent(html);
  });
}, [editor]);


  const handleSubmit = async () => {
    console.log("[BlogEditor] Submit started");
    console.log("[BlogEditor] Title:", title);
    console.log("[BlogEditor] Category:", category);
    console.log("[BlogEditor] Content length:", content?.length);
    
    if (!user) {
      console.log("[BlogEditor] No user found, redirecting");
      return navigate("/login?redirect=blog-editor");
    }
    
    if (!title.trim()) {
      console.log("[BlogEditor] Empty title");
      alert("Please enter a title for your blog post");
      return;
    }

    if (!category) {
      console.log("[BlogEditor] No category selected");
      alert("Please select a category for your blog post");
      return;
    }

    const strippedContent = content.replace(/<[^>]*>/g, '').trim(); // remove HTML tags

    if (!strippedContent || strippedContent.length < 10) {
      console.log("[BlogEditor] Content too short");
      alert("Please add more content to your blog post Content too short");
      return;
    }


    if (!content || content.length < 10) {
      console.log("[BlogEditor] Content too short");
      alert("Please add some content to your blog post");
      return;
    }

    setLoading(true);
    setProgress(0);
    setError(null);

    try {
      for (let i = 0; i <= 100; i += 10) {
        await new Promise((res) => setTimeout(res, 100));
        setProgress(i);
        console.log("[BlogEditor] Upload progress:", i);
      }

      const postData = {
        title,
        content,
        category,
        author: user.displayName || user.email || "Anonymous",
        uid: user.uid,
        imageUrl: user.photoURL || "https://www.flaticon.com/free-icon/user_149071",
        createdAt: new Date(),
      };

      console.log("[BlogEditor] Saving post:", { ...postData, content: "..." });
      const docRef = await addDoc(collection(db, "posts"), postData);
      console.log("[BlogEditor] Post saved successfully. ID:", docRef.id);

      alert("Blog post saved successfully!");
      localStorage.removeItem("editorContent");
      navigate("/");
    } catch (err) {
      console.error("[BlogEditor] Save error:", err);
      setError(err.message);
      console.error("[BlogEditor] Error saving post:", err);
      alert("Failed to save blog post.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gray-100 min-h-screen pt-8 px-2">
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <p className="text-lg font-semibold">Uploading...</p>
            <div className="mt-4 w-64">
              <div className="w-full bg-gray-200 rounded-full h-4">
                <div
                  className="bg-blue-500 h-4 rounded-full"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-center mt-2">{progress}%</p>
            </div>
          </div>
        </div>
      )}

      <div className="w-full max-w-screen-xl mx-auto flex flex-row items-start gap-6">
        {/* Left Column - Fixed width on all screens */}
        <div className="w-[50px] sm:w-[120px] md:w-[150px] flex-shrink-0 my-auto">
          <MenuBar editor={editor} />
        </div>

        {/* Right Column - Grows to fill space */}
        <div className="flex-grow max-h-[80vh] overflow-y-auto  rounded-lg p-2">
          {/* Blog Title */}
          <div className="mb-4">
            <label htmlFor="title" className="block text-lg font-semibold mb-2">
              Blog Title
            </label>
            <input
              id="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full border px-3 py-2 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter blog title..."
            />
          </div>

          {/* Editor */}
          <EditorContent
            editor={editor}
            className="p-2 bg-white rounded shadow min-h-[200px]"
          />

          {/* Bottom Controls */}
          <div className="flex justify-center items-center gap-4 mt-8 mb-4">
            <select
              id="category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="border px-3 py-3 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              required
            >
              <option value="">Select a category...</option>
              {BLOG_CATEGORIES.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
            
            <button
              onClick={handleSubmit}
              className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium"
            >
              Publish Blog Post
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TextEditor;
